import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { WebApiModel } from '../../../models/web-api.model';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { ProfileService } from '../../../services/profile.service';
import { RouterHelperService } from '../../../services/router-helper.service';
import { SeoService } from '../../../services/seo.service';
import { SettingsService } from '../../../services/settings.service';
import { StateService } from '../../../services/state.service';
import { StateRxService } from '../../../services/staterx.service';
import { UiService } from '../../../services/ui.service';
import { FootnoteService } from '../../footnotes/footnote.service';
import { PopupComponent } from '../../popup/popup.component';

@Component({
  selector: 'hcf-place-profile',
  templateUrl: './place-profile.component.html',
  styleUrls: ['./place-profile.component.scss'],
  standalone: false,
})
export class PlaceProfileComponent implements OnInit, OnDestroy {
  place: WebApiModel.Place;
  location: WebApiModel.Location;
  hasServices: boolean;
  hasPeople: boolean;
  selectedTab: string;
  detailsHidden: boolean[] = [];
  tabType = { services: 'services', doctors: 'doctors' };
  numberOfServiceCategoriesByService: number = 0;
  numberOfServiceCategoriesByPerson: number = 0;
  placeServiceCategoriesByService: WebApiModel.ServiceCategory[] = [];
  placeServiceCategoriesByPerson: WebApiModel.ServiceCategory[] = [];
  mapExpanded: boolean = true;
  private _subscriptionIsMobileLayout: Subscription;
  footnotes: string[] = [];
  settings = SettingsService;

  @ViewChild('reportProblemPopupComponent')
  reportProblemPopupComponent: PopupComponent;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _profileService: ProfileService,
    private _stateService: StateService,
    private _stateRxService: StateRxService,
    private _errorHandlingService: ErrorHandlingService,
    private _uiService: UiService,
    private _footnoteService: FootnoteService,
    private _gaService: GoogleAnalyticsService,
    private _seoService: SeoService,
  ) {}

  ngOnInit() {
    this._route.params.subscribe((params: Params) => {
      this.getData(params);
    });

    // Collapse map on mobile by default
    if (this._uiService.isMobile()) {
      this.mapExpanded = false;
    }

    // If browser is resized from mobile to desktop we need to show the map if it was hidden.
    // We need this becasue of ngIf used to collapse map on mobile.
    // We need ngIf to collapse/expand the map because otherwise it's not initialised properly when it's hidden.
    this._subscriptionIsMobileLayout = this._stateRxService.mobileLayoutChanged.rxSubscribe((isMobile) => {
      this.mapExpanded = !isMobile;
    });
  }

  getData(params: Params) {
    this._stateService.isComponentLoading(true);
    this._profileService.getPlace(params[RouterHelperService.urlParam.id]).subscribe(
      (response: WebApiModel.Place) => {
        if (response) {
          this._stateService.isComponentLoading(false);
          this.place = response;
          this.initialiseTabVariables();
          this._uiService.scrollToTop();
          this._gaService.sendPageViewEvent(this._router.url);
        } else {
          this._errorHandlingService.friendlyErrorHandler(true);
        }
      },
      () => {
        this._errorHandlingService.friendlyErrorHandler(true);
      },
    );
  }

  private initialiseTabVariables(): void {
    if (!this.place) {
      return;
    }

    this.footnotes = this._footnoteService.initPlaceFootnotes(this.place);

    const filteredPlaceServiceCategoriesByService: WebApiModel.ServiceCategory[] = [];
    const filteredPlaceServiceCategoriesByPerson: WebApiModel.ServiceCategory[] = [];

    // Some service categories don't have services. Check number of servcies in each category,
    // if at least one of them have some then we can set this.hasServices to true.
    // this.hasServices is used to show/hide the Servcies tab.
    // Also skip service categories without services, populate new array with only servcie categories that have services.
    this.hasServices = false;
    this.hasPeople = false;
    if (this.place.serviceCategories !== null && this.place.serviceCategories.length > 0) {
      /* eslint-disable @typescript-eslint/prefer-for-of */
      for (let i = 0; i < this.place.serviceCategories.length; i++) {
        const currentServiceCategory = this.place.serviceCategories[i];

        if (currentServiceCategory.services && currentServiceCategory.services.length > 0) {
          this.hasServices = true;
          filteredPlaceServiceCategoriesByService.push(currentServiceCategory);
        }

        if (this.serviceCategoryHasPeople(currentServiceCategory.name)) {
          this.hasPeople = true;
          filteredPlaceServiceCategoriesByPerson.push(currentServiceCategory);
        }
      }
    }

    this.placeServiceCategoriesByService = filteredPlaceServiceCategoriesByService;
    this.placeServiceCategoriesByPerson = filteredPlaceServiceCategoriesByPerson;

    this.numberOfServiceCategoriesByService = this.placeServiceCategoriesByService.length;
    this.numberOfServiceCategoriesByPerson = this.placeServiceCategoriesByPerson.length;

    if (this.hasPeople) {
      this.selectedTab = this.tabType.doctors; // Will be selected by default
    } else if (this.hasServices) {
      this.selectedTab = this.tabType.services;
    }

    this._seoService.setTitle(this.place.practiceName);
    this._seoService.setMetaDescription(
      `${this.place.practiceName} ${this.place.receptionLocation || ''}
             ${this.place.address || ''} ${this.place.suburb || ''} ${this.place.city || ''}, ${this.place.postcode || ''}`,
    );

    this.initialiseMap();
  }

  private initialiseMap() {
    const locationDetail = <WebApiModel.LocationDetail>{
      id: this.place.id,
      practiceName: this.place.practiceName,
      receptionLocation: this.place.receptionLocation,
      isAffiliatedProvider: this.place.isAffiliatedProvider,
      isEasyClaim: this.place.isEasyClaim,
      webAddress: this.place.webAddress,
      isConsultant: this.place.isConsultant,
    };
    this.location = <WebApiModel.Location>{
      latitude: this.place.latitude,
      longitude: this.place.longitude,
      address: this.place.address,
      suburb: this.place.suburb,
      city: this.place.city,
      postcode: this.place.postcode,
      isAffiliatedProvider: this.place.isAffiliatedProvider,
      isEasyClaim: this.place.isEasyClaim,
      locationDetails: [locationDetail],
    };
  }

  /**
   * Check if a particular servcie category has any associated people
   */
  private serviceCategoryHasPeople(serviceCategoryName: string): boolean {
    if (!serviceCategoryName) {
      return false;
    }

    /* eslint-disable @typescript-eslint/prefer-for-of */
    for (let p = 0; p < this.place.people.length; p++) {
      const currentPerson = this.place.people[p];
      /* eslint-disable @typescript-eslint/prefer-for-of */
      for (let s = 0; s < currentPerson.serviceCategories.length; s++) {
        const currentPersonServcieCategory = currentPerson.serviceCategories[s];
        if (currentPersonServcieCategory.name === serviceCategoryName) {
          return true;
        }
      }
    }

    return false;
  }

  onTabCLick(selectedTab: string): void {
    this.selectedTab = selectedTab;
  }

  setDetailsVisible(index: number) {
    this.detailsHidden[index] = !this.detailsHidden[index];
  }

  toggleMap() {
    this.mapExpanded = !this.mapExpanded;
  }

  ngOnDestroy() {
    // prevent memory leak when component destroyed
    this._subscriptionIsMobileLayout.unsubscribe();
  }

  reportProblem() {
    this.reportProblemPopupComponent.openPopup();
  }
}
